import { Link, Outlet, useRouterState, useSearch } from "@tanstack/react-router";
import { MotionConfig, motion, useReducedMotion } from "motion/react";
import { type InputHTMLAttributes, type ReactNode, useState } from "react";
import { ArrowRightIcon, EyeClosedIcon, EyeIcon } from "../../components/icons";
import { LegalLinks } from "../legal/LegalPages";

/** Brand side of the card for each screen; sign-up puts it on the right. */
function brandFor(path: string) {
  if (path === "/registro") {
    return {
      side: "right" as const,
      title: "¿Ya tienes cuenta?",
      text: "Entra al panel de tu empresa con tu correo y tu contraseña.",
      cta: { to: "/login" as const, label: "Iniciar sesión" },
    };
  }
  if (path === "/login") {
    return {
      side: "left" as const,
      title: "¿Primera vez en Orbis?",
      text: "Crea tu cuenta con el correo al que llegó tu invitación.",
      cta: { to: "/registro" as const, label: "Crear cuenta" },
    };
  }
  return {
    side: "left" as const,
    title: "Tu cuenta, protegida",
    text: "Atiende el WhatsApp de tu negocio con tu equipo y una IA que solo habla de lo tuyo.",
    cta: null,
  };
}

/** The diagonal edge where the brand side meets the form, mirrored per side. */
const EDGE = {
  left: "polygon(0% 0%, 100% 0%, 88% 100%, 0% 100%)",
  right: "polygon(12% 0%, 100% 0%, 100% 100%, 0% 100%)",
};

const SLIDE = { type: "spring", stiffness: 260, damping: 32, mass: 0.9 } as const;

export interface AuthBrand {
  side: "left" | "right";
  title: string;
  text: string;
  /** A link or button under the text, e.g. to switch between sign-in and sign-up. */
  action?: ReactNode;
  /** Extra content under the text, e.g. the steps of a setup. */
  extra?: ReactNode;
}

/**
 * Sign-in, sign-up and password screens: a glass card over Orbis blue with an orbit in
 * motion. The brand side slides across the card between sign-in and sign-up.
 */
export function AuthShell() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const search = useSearch({ strict: false }) as { next?: string };
  const brand = brandFor(path);
  return (
    <AuthFrame
      brand={{
        ...brand,
        action: brand.cta && (
          <Link className="auth-pill" to={brand.cta.to} search={search.next ? { next: search.next } : {}}>
            {brand.cta.label}
            <ArrowRightIcon />
          </Link>
        ),
      }}
    >
      <Outlet />
    </AuthFrame>
  );
}

/**
 * The glass card itself, for any screen before the panel: sign-in, the two-step setup,
 * an invitation, choosing a company.
 */
export function AuthFrame({ brand, children }: { brand: AuthBrand; children: ReactNode }) {
  return (
    <MotionConfig reducedMotion="user">
      <div className="auth-shell">
        <OrbitBackdrop />
        <motion.div
          className="auth-glass"
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
        >
          <motion.section
            layout="position"
            transition={SLIDE}
            className={`auth-brand-pane is-${brand.side}`}
            style={{ order: brand.side === "left" ? 0 : 1 }}
            animate={{ clipPath: EDGE[brand.side] }}
          >
            <img className="auth-brand-logo" src="/brand/orbis-logo-negativo.svg" alt="Orbis" />
            <motion.div
              key={brand.title}
              className="auth-brand-copy"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.35 }}
            >
              <h2>{brand.title}</h2>
              <p>{brand.text}</p>
              {brand.extra}
              {brand.action}
            </motion.div>
          </motion.section>
          <motion.section
            layout="position"
            transition={SLIDE}
            className="auth-form-pane"
            style={{ order: brand.side === "left" ? 1 : 0 }}
          >
            {children}
          </motion.section>
        </motion.div>
        <LegalLinks />
      </div>
    </MotionConfig>
  );
}

/** Steps of a setup on the brand side: done ones checked, the current one lit. */
export function AuthSteps({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="auth-steps">
      {steps.map((label, i) => (
        <li key={label} className={i < current ? "is-done" : i === current ? "is-current" : undefined}>
          <span aria-hidden="true">{i < current ? "✓" : i + 1}</span>
          {label}
          {i === current && <span className="sr-only"> (paso actual)</span>}
        </li>
      ))}
    </ol>
  );
}

/** One sphere per orbit: size, look and where it starts (a fraction of the turn). */
const ORBITS = [
  { r: 600, tilt: -14, squash: 0.34, speed: 52, size: 16, look: "cyan", start: 0.1 },
  { r: 470, tilt: 20, squash: 0.42, speed: 40, size: 11, look: "pearl", start: 0.55 },
  { r: 360, tilt: -34, squash: 0.58, speed: 30, size: 9, look: "deep", start: 0.3 },
] as const;

/**
 * Slow orbits behind the card, like the logo's, with lit spheres travelling on them.
 * A sphere grows and brightens on the near side of its orbit and shrinks and dims on the
 * far side, so the flat drawing reads as depth. Still for reduced motion.
 */
function OrbitBackdrop() {
  const still = useReducedMotion();
  return (
    <svg className="auth-orbits" viewBox="-500 -500 1000 1000" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        {/* Light from the top left: bright highlight, coloured body, dark night side. */}
        <radialGradient id="sphere-cyan" cx="0.36" cy="0.32" r="0.75">
          <stop offset="0" stopColor="#ecfeff" />
          <stop offset="0.2" stopColor="#67e8f9" />
          <stop offset="0.5" stopColor="#22d3ee" />
          <stop offset="0.8" stopColor="#0e7490" />
          <stop offset="1" stopColor="#082f49" />
        </radialGradient>
        <radialGradient id="sphere-deep" cx="0.36" cy="0.32" r="0.75">
          <stop offset="0" stopColor="#c4f4ff" />
          <stop offset="0.3" stopColor="#2aa9c9" />
          <stop offset="0.7" stopColor="#0b4a6e" />
          <stop offset="1" stopColor="#071a33" />
        </radialGradient>
        <radialGradient id="sphere-pearl" cx="0.36" cy="0.32" r="0.75">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.4" stopColor="#dbe7f5" />
          <stop offset="0.85" stopColor="#6f82a1" />
          <stop offset="1" stopColor="#2b3650" />
        </radialGradient>
        <radialGradient id="sphere-halo">
          <stop offset="0" stopColor="#22d3ee" stopOpacity="0.35" />
          <stop offset="1" stopColor="#22d3ee" stopOpacity="0" />
        </radialGradient>
      </defs>
      {ORBITS.map((o) => {
        const ry = o.r * o.squash;
        // Clockwise from the right: the lower half (near side) first, then the upper (far side).
        const path = `M ${o.r} 0 A ${o.r} ${ry} 0 1 1 ${-o.r} 0 A ${o.r} ${ry} 0 1 1 ${o.r} 0`;
        const angle = o.start * 2 * Math.PI;
        const begin = `${-o.start * o.speed}s`;
        return (
          <g key={o.r} transform={`rotate(${o.tilt})`}>
            <ellipse rx={o.r} ry={ry} className="auth-orbit-ring" />
            <g transform={still ? `translate(${o.r * Math.cos(angle)} ${ry * Math.sin(angle)})` : undefined}>
              {!still && <animateMotion dur={`${o.speed}s`} begin={begin} repeatCount="indefinite" path={path} />}
              <g>
                {!still && (
                  <>
                    <animateTransform
                      attributeName="transform"
                      type="scale"
                      values="1;1.4;1;0.65;1"
                      keyTimes="0;0.25;0.5;0.75;1"
                      dur={`${o.speed}s`}
                      begin={begin}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.9;1;0.9;0.45;0.9"
                      keyTimes="0;0.25;0.5;0.75;1"
                      dur={`${o.speed}s`}
                      begin={begin}
                      repeatCount="indefinite"
                    />
                  </>
                )}
                <circle r={o.size * 3} fill="url(#sphere-halo)" />
                <circle r={o.size} fill={`url(#sphere-${o.look})`} />
                {/* Thin rim of reflected light on the dark side. */}
                <circle r={o.size - 0.5} fill="none" stroke="rgb(103 232 249 / 0.35)" strokeWidth="1" />
              </g>
            </g>
          </g>
        );
      })}
    </svg>
  );
}

/** Title block and content of one screen; its parts rise in one after another. */
export function AuthLayout({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string;
  subtitle: string;
  icon?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="auth-pane auth-stagger" key={title}>
      {icon}
      <div className="auth-heading">
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

/** Field with an icon and a line that lights up on focus; passwords get show/hide. */
export function AuthField({
  id,
  label,
  icon,
  type = "text",
  ...input
}: { id: string; label: string; icon: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  const [shown, setShown] = useState(false);
  const isPassword = type === "password";
  return (
    <div className="auth-field">
      <label htmlFor={id}>{label}</label>
      <div className="auth-input">
        <span className="auth-input-icon">{icon}</span>
        <input id={id} type={isPassword && shown ? "text" : type} {...input} />
        {isPassword && (
          <button
            type="button"
            className="auth-eye"
            aria-label={shown ? "Ocultar contraseña" : "Mostrar contraseña"}
            aria-pressed={shown}
            onClick={() => setShown(!shown)}
          >
            {shown ? <EyeClosedIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
    </div>
  );
}

/** Checkbox in the glass style: the check draws itself in. */
export function AuthCheck({
  checked,
  onChange,
  children,
  hint,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="auth-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="auth-check-box" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5.5 12.5L10 17L18.5 7.5" />
        </svg>
      </span>
      <span className="auth-check-text">
        {children}
        {hint && <small>{hint}</small>}
      </span>
    </label>
  );
}

/** Main button with a spinner while it works. */
export function AuthSubmit({ busy, children, disabled }: { busy: boolean; children: ReactNode; disabled?: boolean }) {
  return (
    <button className="auth-submit" type="submit" disabled={busy || disabled} aria-busy={busy}>
      {busy && <span className="auth-spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

/** The card fades out before the panel opens, so the change does not jump. */
export async function leaveAuth() {
  const card = document.querySelector<HTMLElement>(".auth-glass");
  if (!card || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  await card.animate(
    [
      { opacity: 1, transform: "scale(1)" },
      { opacity: 0, transform: "scale(0.97)" },
    ],
    { duration: 240, easing: "cubic-bezier(0.23, 1, 0.32, 1)", fill: "forwards" },
  ).finished;
}
